/**
 * One binning policy for every continuous distribution drawn in TypeScript.
 *
 * This is a faithful port of `apps/python-api/canonical_analysis/continuous_statistics.py`,
 * which is the source of truth for the grid a continuous answer is drawn on. Before
 * this file, the Survey data tab carried two independent copies of a DIFFERENT and
 * unfixed rule: an inline `getNiceStep` in `NumericChart.tsx` for the segmented
 * sparklines and another inside `aggregateSimple` in `lib/aggregation.ts` for the
 * unsegmented one. Both targeted 8 bins with a `normalized <= x` ladder, neither cut
 * the tail, neither capped the bin count, and neither knew what a bounded unit is, so
 * the same question was binned one way in the deck and another way in the tab.
 *
 * The rules that must not drift from the Python, because the fixture asserts them
 * from both languages:
 *   - the step comes from the 1 / 2 / 2.5 / 5 x 10^k family, chosen by distance to
 *     `TARGET_BIN_COUNT`, and **ties go to the coarser step** (D2: a span of 65 over a
 *     target of 10 is 13 bins at step 5 and 7 bins at step 10, both 3 away, and the
 *     answer is 10);
 *   - the long tail is cut at `max(p95, q3 + 1.5 * IQR)` and folded into one overflow
 *     bucket only when it compresses at least `OVERFLOW_MIN_STEP_MULTIPLE` steps;
 *   - a bounded unit keeps its own domain and takes no cut;
 *   - percentiles are Hyndman-Fan type 7, so p50 is the printed median.
 *
 * Parity is proven, not asserted: `tests/fixtures/continuous-binning/parity.v1.json`
 * holds the input vectors and the expected edges, and it is read by
 * `packages/deck-ui/src/utils/__tests__/continuousBinning.test.ts` (vitest) and by
 * `tests/scripts/template-batch-launch/judges/test_j2_numeric.py` (pytest, against
 * both the producer and the J2 judge's own mirror). A change to the policy in any one
 * of the three implementations fails in the other two.
 *
 * The left edge is floored onto the step grid, so a distribution whose smallest
 * answer is 7.81 opens on a `[0, 10)` bucket. That is deliberate and pinned by the
 * `first_bin_floors_to_the_grid` vector: a bucket is not a window, the same grid has
 * to hold every segment of one question, and an edge placed at whoever answered
 * lowest would move every boundary when the filter changes. Callers that need to say
 * where the answers actually start print the observed range instead.
 */

/** Target and ceiling for the number of drawn bins, the overflow bucket included. */
export const TARGET_BIN_COUNT = 10
export const MAX_BIN_COUNT = 10

/**
 * Below this many respondents a base is low: the reading is directional. The
 * client-side mirror of the one display owner, `kpi_rows.LOW_BASE_DISPLAY_FLOOR`
 * (D5), for the Quick Study chart that counts raw answers in the browser and so
 * has no stamped status to read. Pinned to the Python owner through
 * `lowBaseDisplayFloor` in the shared parity fixture; it changes what a surface
 * says about a base, never how the base is binned.
 */
export const LOW_BASE_DISPLAY_FLOOR = 30

/** The low-base mark for a counted base, as `kpi_rows.is_low_reader_base`. */
export function isLowReaderBase(count: number): boolean {
  return count <= 0 || count < LOW_BASE_DISPLAY_FLOOR
}

/**
 * Coerce a scalar into a finite number, or `null`.
 *
 * A dict (twin-monadic / matrix / ranking map) is never a numeric answer:
 * `Number({})` is `NaN`, but `String({})` is `[object Object]`. Callers that
 * bin or average must skip the map and split by `monadicLaneScope` first.
 */
export function asFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'object') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/** An overflow bucket must fold at least this many step-widths of tail together. */
export const OVERFLOW_MIN_STEP_MULTIPLE = 2.0

/** Units whose scale is the question's own domain rather than the answers'. */
export const BOUNDED_UNIT_BOUNDS: Record<string, [number, number]> = {
  percent_0_100: [0.0, 100.0],
  index_0_100: [0.0, 100.0],
  probability_0_1: [0.0, 1.0],
}

const NICE_MULTIPLIERS = [1.0, 2.0, 2.5, 5.0]
const EPS = 1e-9
const EDGE_SIGNIFICANT_DIGITS = 12

/** One bucket, half-open `[low, high)`, with the last one inclusive. */
export interface ContinuousBin {
  low: number
  high: number
}

/** The whole edge decision for one distribution, as drawn. */
export interface ContinuousBinning {
  edges: Array<ContinuousBin>
  step: number
  cut: number
  hasOverflow: boolean
}

/** The declared domain of a continuous question, as the Python metric carries it. */
export interface ContinuousDomain {
  unit?: string
  normalization?: { min?: number | null; max?: number | null }
}

function isFiniteNumber(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return false
  }
  const numeric = typeof value === 'number' ? value : Number(value)
  return typeof value === 'number' && Number.isFinite(numeric)
}

/** `float(f"{value:.12g}")`: 12 significant digits, so an accumulated edge is clean. */
function cleanEdge(value: number): number {
  if (!Number.isFinite(value)) {
    return value
  }
  if (value === 0) {
    return 0
  }
  return Number(value.toPrecision(EDGE_SIGNIFICANT_DIGITS))
}

/** `round(value, 12)`: 12 DECIMAL places, matching the Python step construction. */
function roundToTwelveDecimals(value: number): number {
  if (!Number.isFinite(value) || Math.abs(value) >= 1e21) {
    return value
  }
  return Number(value.toFixed(12))
}

function floorTo(value: number, step: number): number {
  return cleanEdge(Math.floor(value / step + EPS) * step)
}

function ceilTo(value: number, step: number, floor?: number): number {
  const snapped = cleanEdge(Math.ceil(value / step - EPS) * step)
  if (floor === undefined) {
    return snapped
  }
  return Math.max(snapped, cleanEdge(floor))
}

function binCountFor(span: number, step: number): number {
  return Math.max(1, Math.ceil(span / step - EPS))
}

/** Every nice step within two decades of `span`, smallest first. */
function stepCandidates(span: number, integralOnly: boolean): Array<number> {
  const exponent = span <= 0 ? 0 : Math.floor(Math.log10(Math.abs(span)))
  const steps = new Set<number>()
  for (let power = exponent - 2; power <= exponent + 2; power++) {
    const magnitude = Math.pow(10, power)
    for (const multiplier of NICE_MULTIPLIERS) {
      const step = roundToTwelveDecimals(multiplier * magnitude)
      if (step <= 0) {
        continue
      }
      if (integralOnly && !Number.isInteger(step)) {
        continue
      }
      steps.add(step)
    }
  }
  let ordered = Array.from(steps).sort((left, right) => left - right)
  if (integralOnly) {
    const whole = ordered.filter((step) => step >= 1)
    ordered = whole.length > 0 ? whole : [1.0]
  }
  return ordered
}

/**
 * The 1 / 2 / 2.5 / 5 x 10^k step that lands closest to `targetBins`.
 *
 * Ties go to the coarser step: fewer, rounder bars read better than one extra sliver.
 * `integralOnly` drops fractional steps so an all-integer measure never gets a 2.5-wide
 * bucket. A span of zero has no scale to read, so the smallest allowed step stands in.
 */
export function niceStep(
  span: number,
  targetBins: number,
  options?: { integralOnly?: boolean },
): number {
  if (targetBins < 1) {
    throw new Error(`targetBins must be at least 1, got ${targetBins}`)
  }
  const integralOnly = options?.integralOnly === true
  const candidates = stepCandidates(span, integralOnly)
  if (span <= 0) {
    return candidates[0]
  }
  // A step wider than the whole span draws one bar however coarse it is, so every
  // such step ties on bin count and "coarser wins" would hand a span of 1 a bucket
  // 500 wide. They are not candidates for this span at all.
  const within = candidates.filter((step) => step <= span * (1 + EPS))
  const pool = within.length > 0 ? within : candidates
  let best = pool[0]
  let bestDistance = Math.abs(binCountFor(span, best) - targetBins)
  for (const step of pool) {
    const distance = Math.abs(binCountFor(span, step) - targetBins)
    // Strictly-better only, walking ascending steps: equal distance keeps the larger
    // step, which is Python's `key=(distance, -step)` under a stable `min`.
    if (distance < bestDistance || (distance === bestDistance && step > best)) {
      best = step
      bestDistance = distance
    }
  }
  return best
}

/**
 * The type 7 percentile of `values` at fraction `p` (0-1, not 0-100).
 *
 * At the exact midpoint the two neighbours are averaged rather than interpolated,
 * which is the only form that is bit-identical to the published median for every
 * even n. Empty input has no percentile and returns null.
 */
export function percentile(values: Array<number>, p: number): number | null {
  if (!(p >= 0 && p <= 1)) {
    throw new Error(`percentile p must be a fraction in 0..1, got ${p}`)
  }
  const ordered = values.map(Number).sort((left, right) => left - right)
  const count = ordered.length
  if (count === 0) {
    return null
  }
  if (count === 1) {
    return ordered[0]
  }
  const position = (count - 1) * p
  const lowerIndex = Math.floor(position)
  const fraction = position - lowerIndex
  if (fraction === 0) {
    return ordered[lowerIndex]
  }
  const lower = ordered[lowerIndex]
  const upper = ordered[lowerIndex + 1]
  if (fraction === 0.5) {
    return (lower + upper) / 2
  }
  return lower + fraction * (upper - lower)
}

function boundedUnitBounds(domain?: ContinuousDomain): [number, number] | null {
  const native = domain?.unit === undefined ? undefined : BOUNDED_UNIT_BOUNDS[String(domain.unit)]
  if (native === undefined) {
    return null
  }
  const minimum = domain?.normalization?.min
  const maximum = domain?.normalization?.max
  if (
    minimum !== undefined && minimum !== null &&
    maximum !== undefined && maximum !== null &&
    Number(maximum) > Number(minimum)
  ) {
    return [Number(minimum), Number(maximum)]
  }
  return native
}

function tailCut(values: Array<number>, observedMin: number, observedMax: number): number {
  const q3 = percentile(values, 0.75) as number
  const iqr = q3 - (percentile(values, 0.25) as number)
  const fence = q3 + 1.5 * iqr
  const p95 = percentile(values, 0.95) as number
  const cut = Math.max(p95, fence)
  return Math.min(Math.max(cut, observedMin), observedMax)
}

interface BuildRequest {
  step: number
  left: number
  cutRaw: number
  observedMax: number
  snapLeft: boolean
  perValue: boolean
}

function buildBinning(request: BuildRequest): ContinuousBinning {
  const { step, left, cutRaw, observedMax, snapLeft, perValue } = request
  const start = snapLeft ? floorTo(left, step) : cleanEdge(left)
  const firstCut = ceilTo(cutRaw, step, start + step)
  const count = binCountFor(firstCut - start, step)
  const edges: Array<ContinuousBin> = []
  for (let index = 0; index < count; index++) {
    edges.push({
      low: cleanEdge(start + index * step),
      high: cleanEdge(start + (index + 1) * step),
    })
  }
  const cut = edges[edges.length - 1].high
  const hasOverflow = observedMax > cut + OVERFLOW_MIN_STEP_MULTIPLE * step + EPS
  if (hasOverflow) {
    // The observed maximum is a datum, not a grid point: it goes in exactly as
    // measured, so no rounding can nudge the edge below the answer it exists to hold.
    edges.push({ low: cut, high: observedMax })
  } else {
    // One answer past the cut is not a tail, it is the next bar along. On a discrete
    // measure the grid runs PAST the maximum so each value gets its own bar.
    const limit = perValue ? observedMax + EPS : observedMax - EPS
    while (edges[edges.length - 1].high <= limit) {
      const low = edges[edges.length - 1].high
      edges.push({ low, high: cleanEdge(low + step) })
    }
  }
  return { edges, step, cut, hasOverflow }
}

interface CapRequest {
  firstStep: number
  spanHint: number
  integralOnly: boolean
  left: number
  cutRaw: number
  observedMax: number
  snapLeft: boolean
}

function cappedBinning(request: CapRequest): ContinuousBinning {
  const { firstStep, spanHint, integralOnly, left, cutRaw, observedMax, snapLeft } = request
  const wider = stepCandidates(spanHint, integralOnly).filter((step) => step >= firstStep)
  const candidates = wider.length > 0 ? wider : [firstStep]
  let binning: ContinuousBinning | null = null
  for (const step of candidates) {
    binning = buildBinning({
      step,
      left,
      cutRaw,
      observedMax,
      snapLeft,
      perValue: snapLeft && integralOnly,
    })
    if (binning.edges.length <= MAX_BIN_COUNT) {
      return binning
    }
  }
  return binning as ContinuousBinning
}

/**
 * Round bin edges for one continuous distribution, with the long tail cut.
 *
 * Bounded-scale units keep their declared or native bounds and take no cut. On an
 * open unit the observed answers decide the domain, because binning to a declared
 * `max: 5000` on a question nobody answered above 900 is how a chart ends up four
 * fifths dead ink. The step is read off the cut span, never off the observed range,
 * or one runaway answer sets everyone else's bucket width.
 */
export function continuousBinEdges(
  values: Array<number>,
  domain?: ContinuousDomain,
): ContinuousBinning {
  const numbers: Array<number> = []
  for (const value of values) {
    if (isFiniteNumber(value)) {
      numbers.push(Number(value))
    }
  }
  if (numbers.length === 0) {
    return { edges: [], step: 0, cut: 0, hasOverflow: false }
  }

  let observedMin = numbers[0]
  let observedMax = numbers[0]
  let integralOnly = true
  for (const value of numbers) {
    if (value < observedMin) {
      observedMin = value
    }
    if (value > observedMax) {
      observedMax = value
    }
    if (!Number.isInteger(value)) {
      integralOnly = false
    }
  }

  const bounds = boundedUnitBounds(domain)
  if (bounds !== null) {
    // The bounds are the grid here, so the integral-step rule does not apply: a
    // probability question answered only in 0s and 1s is still a 0-1 scale in tenths.
    const [minimum, maximum] = bounds
    return cappedBinning({
      firstStep: niceStep(maximum - minimum, TARGET_BIN_COUNT),
      spanHint: maximum - minimum,
      integralOnly: false,
      left: minimum,
      cutRaw: maximum,
      observedMax,
      snapLeft: false,
    })
  }

  const cutRaw = tailCut(numbers, observedMin, observedMax)
  // Every answer identical is a real result, not an empty one. It has no spread to
  // read a scale off, so it draws as a single bar holding everyone.
  const spanHint = cutRaw - observedMin || 1.0
  let step = niceStep(spanHint, TARGET_BIN_COUNT, { integralOnly })
  // The step belongs to the span it will actually be drawn over, so re-read it once
  // the left edge and the cut have been snapped onto the grid the first step implies.
  for (let pass = 0; pass < 3; pass++) {
    const left = floorTo(observedMin, step)
    const cut = ceilTo(cutRaw, step, left + step)
    if (cut - left < 2 * step) {
      break
    }
    const refined = niceStep(cut - left, TARGET_BIN_COUNT, { integralOnly })
    if (refined === step) {
      break
    }
    step = refined
  }
  return cappedBinning({
    firstStep: step,
    spanHint,
    integralOnly,
    left: observedMin,
    cutRaw,
    observedMax,
    snapLeft: true,
  })
}

/** Shared membership for histogram counts and filters using the same edges. */
export function isInContinuousBin(value: number, edge: ContinuousBin, inclusiveHigh: boolean): boolean {
  return value >= edge.low && (inclusiveHigh ? value <= edge.high : value < edge.high)
}

/**
 * How many answers fall in each bucket, half-open with the last inclusive.
 *
 * One implementation, because the segmented sparklines and the unsegmented histogram
 * have to be provably the same buckets: two counters drift, and two segments counted
 * on different boundaries compare different things side by side without saying so.
 */
export function binCounts(values: Array<number>, edges: Array<ContinuousBin>): Array<number> {
  if (edges.length === 0) {
    return []
  }
  const last = edges.length - 1
  return edges.map((edge, index) =>
    values.filter((value) => isInContinuousBin(value, edge, index === last)).length,
  )
}

/**
 * Default bound formatter: whole numbers on a step of 1 or more, one decimal below.
 * Callers that print money pass their own so the symbol reaches every figure.
 */
export function formatBinBound(value: number, step: number): string {
  if (step >= 1 || Number.isInteger(value)) {
    return String(Math.round(value))
  }
  return value.toFixed(1)
}

/**
 * One label per bucket. The overflow bucket is open-ended ("1,200+"): its true
 * ceiling is the single most extreme answer, which is not a fact about the group.
 * No en dash, in any locale: the separator is the word "to".
 */
export function continuousBinLabels(
  binning: ContinuousBinning,
  format?: (value: number) => string,
): Array<string> {
  const bound = format ?? ((value: number) => formatBinBound(value, binning.step))
  const last = binning.edges.length - 1
  return binning.edges.map((edge, index) => {
    if (binning.hasOverflow && index === last) {
      return `${bound(edge.low)}+`
    }
    return `${bound(edge.low)} to ${bound(edge.high)}`
  })
}

/**
 * The declared continuous domain compiled onto a SurveyJS element, or undefined.
 *
 * Read from the canonical block the compiler stamps (`_canonical.response`), never
 * from the question's text or id: nothing here may be keyed to a specific survey.
 */
export function readContinuousDomain(question: any): ContinuousDomain | undefined {
  const response = question?._canonical?.response
  if (!response || response.kind !== 'continuous') {
    return undefined
  }
  return {
    unit: typeof response.unit === 'string' ? response.unit : undefined,
    normalization: { min: response.min ?? null, max: response.max ?? null },
  }
}
